var React = require('react');
var { useState, useEffect, useCallback, useMemo } = require('react');
var { connect } = require('react-redux');
var { useLocation, useNavigate } = require('react-router-dom');
var { useTranslation } = require('react-i18next');
var sensorsManager = require('sensorsManager');
var { sensorsMap } = require('sensorsManager');
var dayjs = require('../../utils/dayjsConfig');
var { timeSinceNowString } = require('helpers');
var { parseQueryParams, clearQueryParams } = require('helpers');
var DischargeGraphContainer = require('DischargeGraphContainer');
const SearchContainer = require('./SearchContainer.jsx');
const { derivePressureSamples } = require('./transmitterTestHelpers');
require('transmitterTestCSS');
var { trackPageView } = require('Utils');

// Constants
const FALLBACK_PRESSURE_RANGE_DAYS = 100;

const CLASSIFICATION_COLORS = {
  GOOD: 'var(--color-status-positive-80)',
  FAIR: 'var(--color-status-warning-100)',
  BAD: 'var(--color-status-alert-96)',
  DEFAULT: 'var(--color-grey-34)',
};

const INITIAL_STATE = {
  distribution_name: '',
  farming_unit_name: '',
  field_name: '',
  transmitter_id: '',
  recentAnalogValues: [],
  recentC1Values: [],
  recentRSSIValues: [],
  recentBerValues: [],
  recentRSRQValues: [],
  recentRSRPValues: [],
  recentBatteryValues: [],
  recentWFRSamples: [],
  lastSeen: '',
  simStatus: '',
  modem_hardware: '',
  modem_software: undefined,
  desired_modem_software: undefined,
  iccid: '',
  sensor_batch: '',
  transmission_interval: '',
  sample_interval: '',
  micro_version: '',
  installation_date: '',
  recent_errors: [],
  time_zone: '',
  type: '',
  sub_type: '',
  should_present_graph: false,
  irrigation_hin: '',
  transmitter_name: '',
  pressure_samples: [],
  discharge_samples: [],
  sim_last_event: '',
};

// Helper to get color from classification
const getClassificationColor = (classification) => {
  return CLASSIFICATION_COLORS[classification] || CLASSIFICATION_COLORS.DEFAULT;
};

// Helper to safely get item from array
const getItemAtIndex = (array, index) => {
  if (array && array.length > index) {
    return array[index];
  }
  return { value: '', date: '' };
};

// Sub-component for value rows with classification colors
const ValueRow = ({
  label,
  values,
  showClassification,
  renderValue,
  className,
  isLoading,
}) => {
  const rowStyle = isLoading ? { opacity: 0.5 } : {};

  return (
    <>
      {values.slice(0, 3).map((item, index) => (
        <tr
          key={`${label}-${index}`}
          className={className}
          style={index === 0 ? rowStyle : {}}
        >
          <th scope='row'>{index === 0 ? label : ''}</th>
          <td>{item.date}</td>
          <td>
            {showClassification && item.classification ? (
              <span
                style={{ color: getClassificationColor(item.classification) }}
              >
                {renderValue ? renderValue(item) : item.value}
              </span>
            ) : renderValue ? (
              renderValue(item)
            ) : (
              item.value
            )}
          </td>
        </tr>
      ))}
      {/* Fill empty rows if less than 3 values */}
      {Array.from({ length: Math.max(0, 3 - values.length) }).map(
        (_, index) => (
          <tr key={`${label}-empty-${index}`} className={className}>
            <th scope='row'>
              {values.length === 0 && index === 0 ? label : ''}
            </th>
            <td></td>
            <td></td>
          </tr>
        ),
      )}
    </>
  );
};

// Sub-component for connectivity values (RSSI, RSRP, RSRQ, BER)
const ConnectivityRow = ({
  rssiValues,
  rsrpValues,
  rsrqValues,
  berValues,
  index,
  isLoading,
  t,
}) => {
  const rssi = getItemAtIndex(rssiValues, index);
  const rsrp = getItemAtIndex(rsrpValues, index);
  const rsrq = getItemAtIndex(rsrqValues, index);
  const ber = getItemAtIndex(berValues, index);

  const rowStyle = index === 0 && isLoading ? { opacity: 0.5 } : {};

  const renderMetric = (label, value, unit, classification, hasData) => {
    if (!hasData) return null;
    return (
      <span style={{ display: 'inline' }}>
        {label}:{' '}
        <span style={{ color: getClassificationColor(classification) }}>
          {value}
          {unit}
        </span>
      </span>
    );
  };

  return (
    <tr className='table-smoked' style={rowStyle}>
      <th scope='row'>{index === 0 ? t('recent_connectivity_values') : ''}</th>
      <td>{rssi.date}</td>
      <td>
        {renderMetric(
          'RSSI',
          rssi.value,
          'dB',
          rssi.classification,
          rssiValues.length > index,
        )}
        {rsrpValues.length > index && rssiValues.length > index && ', '}
        {renderMetric(
          'RSRP',
          rsrp.value,
          'dBm',
          rsrp.classification,
          rsrpValues.length > index,
        )}
        {rsrqValues.length > index &&
          (rssiValues.length > index || rsrpValues.length > index) &&
          ', '}
        {renderMetric(
          'RSRQ',
          rsrq.value,
          'dB',
          rsrq.classification,
          rsrqValues.length > index,
        )}
        {berValues.length > index &&
          (rssiValues.length > index ||
            rsrpValues.length > index ||
            rsrqValues.length > index) &&
          ', '}
        {berValues.length > index && (
          <span style={{ display: 'inline' }}>
            BER:{' '}
            <span style={{ color: getClassificationColor(ber.classification) }}>
              {ber.value * 100}%
            </span>
          </span>
        )}
      </td>
    </tr>
  );
};

// Sub-component for info rows (no date column)
const InfoRow = ({
  label,
  value,
  isLoading,
  className = 'table-blue-third',
}) => {
  const rowStyle = isLoading ? { opacity: 0.5 } : {};

  if (!value && value !== 0) return null;

  return (
    <tr className={className} style={rowStyle}>
      <th scope='row'>{label}</th>
      <td colSpan='2'>{value}</td>
    </tr>
  );
};

// Loading indicator component
const LoadingIndicator = ({ t }) => (
  <div className='transmitter-test-loading'>
    <span
      className='spinner-border spinner-border-sm'
      role='status'
      aria-hidden='true'
    />
    <span className='transmitter-test-loading-text'>{t('loading')}</span>
  </div>
);

// Empty state component
const EmptyState = ({ t }) => (
  <div className='transmitter-test-empty-state'>
    <div className='transmitter-test-empty-state-icon'>🔍</div>
    <h3 className='transmitter-test-empty-state-title'>
      {t('search_transmitter_title') || 'Search for a Transmitter'}
    </h3>
    <p className='transmitter-test-empty-state-description'>
      {t('search_transmitter_description') ||
        'Enter a transmitter ID or select a field to view transmitter status and metrics.'}
    </p>
  </div>
);

// Error state component
const ErrorState = ({ error, onRetry, t }) => (
  <div className='transmitter-test-error-state'>
    <div className='transmitter-test-error-state-icon'>⚠️</div>
    <h3 className='transmitter-test-error-state-title'>
      {t('error_occurred') || 'An error occurred'}
    </h3>
    <p className='transmitter-test-error-state-description'>{error}</p>
    {onRetry && (
      <button className='btn transmitter-test-retry-btn' onClick={onRetry}>
        {t('retry') || 'Try Again'}
      </button>
    )}
  </div>
);

// Main component
const TransmitterTest = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [data, setData] = useState(INITIAL_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [isPartialLoading, setIsPartialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [partialError, setPartialError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [initialTransmitterId, setInitialTransmitterId] = useState('');
  const urlParamsProcessedRef = React.useRef(false);

  // Computed values
  const modemTitle = useMemo(() => {
    if (
      data.modem_software &&
      data.modem_hardware &&
      data.desired_modem_software
    ) {
      return `${data.modem_hardware} \\ ${data.modem_software} (${t('latest') || 'Latest'}: ${data.desired_modem_software})`;
    }
    return '';
  }, [
    data.modem_hardware,
    data.modem_software,
    data.desired_modem_software,
    t,
  ]);

  // Handle test button click
  const handleTestClick = useCallback(
    (transmitterID) => {
      // Reset state at the start to avoid race conditions
      setData(INITIAL_STATE);
      setIsLoading(true);
      setError(null);
      setPartialError(null);
      setHasSearched(true);

      const cleanId = transmitterID.replace(/\s/g, '');

      // First request - fast response
      sensorsManager
        .getTransmitterStatus(cleanId, false)
        .then((res) => {
          const body = res.body;
          const shouldPresentGraph =
            body.transmitter_type.includes('piezometer') ||
            body.transmitter_type.includes('low_pressure') ||
            body.transmitter_type.includes('high_pressure') ||
            body.transmitter_type.includes('ndrip') ||
            body.transmitter_type.includes('wfr');
          const recentAnalogValues = body.recent_analog_samples.map((x) => ({
            value: x.value,
            date: dayjs(parseInt(x.ts) * 1000).format('L LTS Z'),
          }));

          const recentWFRSamples = body.recent_wfr_samples.map((x) => ({
            volume: x.value,
            discharge: Number.parseFloat(x.discharge).toFixed(2),
            pressure: Number.parseFloat(x.pressure).toFixed(2),
            start_date: dayjs(parseInt(x.ts) * 1000).format('L LTS Z'),
            end_date: dayjs(parseInt(x.end_date) * 1000).format('L LTS Z'),
          }));

          let pressure_samples = derivePressureSamples(body);

          // For e-piezometers / low_pressure / high_pressure with no pressure data, add a zero sample so the graph renders
          if (
            pressure_samples.length === 0 &&
            (body.transmitter_type.includes('piezometer') ||
              body.transmitter_type.includes('low_pressure') ||
              body.transmitter_type.includes('high_pressure'))
          ) {
            pressure_samples = [
              {
                date: dayjs()
                  .subtract(FALLBACK_PRESSURE_RANGE_DAYS, 'day')
                  .format('L LTS Z'),
                value: 0,
              },
              {
                date: dayjs().format('L LTS Z'),
                value: 0,
              },
            ];
          }

          const discharge_samples =
            body.discharge_samples.length !== 0
              ? Object.entries(body.discharge_samples).map((x) => ({
                  date: dayjs(parseInt(x[0]) * 1000).format('L LTS Z'),
                  value: x[1],
                }))
              : [];

          const irrigation_hin =
            pressure_samples.length > 0 ? body.irrigation_hin : undefined;
          const type = sensorsMap.get(body.transmitter_type);
          const sub_type =
            body.transmitter_sub_type === ''
              ? undefined
              : body.transmitter_sub_type;

          setData((prev) => ({
            ...prev,
            transmitter_id: transmitterID,
            recentAnalogValues,
            should_present_graph: shouldPresentGraph,
            pressure_samples,
            discharge_samples,
            recentWFRSamples,
            irrigation_hin,
            transmitter_name: body.sensor_name,
            time_zone: body.time_zone,
            distribution_name: body.distribution.name,
            farming_unit_name: body.farming_unit.name,
            field_name: body.field.name,
            type,
            sub_type,
          }));

          setIsLoading(false);
          setIsPartialLoading(true);

          if (typeof mixpanel !== 'undefined' && mixpanel?.track) {
            mixpanel.track('Transmitter Test Pressed', {
              'Transmitter ID': transmitterID,
              is_pressure_graph_visible: !!recentWFRSamples.length,
            });
          }
        })
        .catch((err) => {
          console.error('Fast request failed:', err);
          setError(
            t('transmitter_not_found') ||
              'Transmitter not found or an error occurred.',
          );
          setIsLoading(false);
          setIsPartialLoading(false);
        });

      // Second request - detailed data
      sensorsManager
        .getTransmitterStatus(cleanId, true)
        .then((res) => {
          const body = res.body;

          let installationDate = '';
          if (body.installation_date) {
            const installationDateObject = dayjs(
              parseInt(body.installation_date) * 1000,
            );
            const timeAgo = timeSinceNowString(installationDateObject);
            installationDate = `${installationDateObject.format('L LTS Z')} (${t(timeAgo[1]).format(timeAgo[0])})`;
          }

          const lastSeenDate = dayjs(parseInt(body.last_seen) * 1000).format(
            'L LTS Z',
          );

          const mapSamples = (samples) =>
            samples.map((x) => ({
              value: x.value,
              date: dayjs(parseInt(x.ts) * 1000).format('L LTS Z'),
              classification: x.classification,
            }));

          setData((prev) => ({
            ...prev,
            recentC1Values: mapSamples(body.recent_c1_samples),
            recentRSSIValues: mapSamples(body.recent_rssi_samples),
            recentBerValues: mapSamples(body.recent_ber_samples),
            recentRSRQValues: mapSamples(body.recent_rsrq_samples),
            recentRSRPValues: mapSamples(body.recent_rsrp_samples),
            recentBatteryValues: mapSamples(body.recent_battery_samples),
            lastSeen: lastSeenDate,
            simStatus: parseInt(body.sim_status),
            modem_hardware: body.modem_hardware,
            modem_software: body.modem_software,
            desired_modem_software: body.desired_modem_software,
            iccid: body.iccid,
            sensor_batch: body.sensor_batch,
            transmission_interval: body.transmission_interval,
            sample_interval: body.sample_interval,
            micro_version: body.micro_version,
            installation_date: installationDate,
            sim_last_event: (body.sim_last_event || '').substring(0, 61),
            recent_errors: body.recent_errors.map((x) => ({
              value: x.value,
              date: dayjs(parseInt(x.ts) * 1000).format('L LTS Z'),
            })),
          }));

          setIsPartialLoading(false);
        })
        .catch((err) => {
          console.error('Detailed request failed:', err);
          setPartialError(
            t('detailed_data_unavailable') ||
              'Some detailed metrics could not be loaded.',
          );
          setIsPartialLoading(false);
        });
    },
    [t],
  );

  // Process URL params on mount
  useEffect(() => {
    trackPageView('TransmitterTest');
  }, []);

  // Handle URL params for deep linking
  useEffect(() => {
    if (urlParamsProcessedRef.current) return;

    const urlParams = parseQueryParams(location);
    const transmitterId = urlParams.get('transmitter_id');
    const originParam = urlParams.get('origin');

    if (transmitterId) {
      urlParamsProcessedRef.current = true;
      setInitialTransmitterId(transmitterId);

      // Track URL access
      if (typeof mixpanel !== 'undefined' && mixpanel?.track) {
        try {
          const eventProps = {
            transmitter_id: transmitterId,
            path: location.pathname,
          };
          if (originParam) {
            eventProps.origin = originParam;
          }
          mixpanel.track('Transmitter Test Via URL', eventProps);
        } catch (e) {
          // swallow tracking errors
        }
      }

      // Auto-run test
      handleTestClick(transmitterId);

      // Clear URL params
      setTimeout(() => {
        clearQueryParams(navigate, location, '/transmitter-test');
      }, 100);
    } else {
      urlParamsProcessedRef.current = true;
    }
  }, [location, navigate, handleTestClick]);

  // Retry handler
  const handleRetry = useCallback(() => {
    if (data.transmitter_id) {
      handleTestClick(data.transmitter_id);
    }
  }, [data.transmitter_id, handleTestClick]);

  // Tab change handler - reset results when switching tabs
  const handleTabChange = useCallback(() => {
    setData(INITIAL_STATE);
    setError(null);
    setHasSearched(false);
  }, []);

  // Render content based on state
  const renderContent = () => {
    if (error) {
      return <ErrorState error={error} onRetry={handleRetry} t={t} />;
    }

    if (!hasSearched) {
      return <EmptyState t={t} />;
    }

    return (
      <>
        {data.should_present_graph && (
          <div
            className='transmitter-test-graph-container'
            style={{ opacity: isLoading ? 0.5 : 1 }}
          >
            <DischargeGraphContainer
              irrigation_hin={data.irrigation_hin}
              samples={data.recentWFRSamples}
              pressure_samples={data.pressure_samples}
              discharge_samples={data.discharge_samples}
              lastSeen={getItemAtIndex(data.recentAnalogValues, 0).date}
              time_zone={data.time_zone}
            />
          </div>
        )}

        <table
          className='table table-hover table-sm transmitter-test-table'
          style={{ opacity: isLoading ? 0.5 : 1 }}
        >
          <thead>
            {data.simStatus === 1 && (
              <tr>
                <td className='bg-green2 text-center text-white' colSpan='3'>
                  {t('sim_activation')}
                </td>
              </tr>
            )}
            <tr className='table-grey45'>
              <th className='table-header-cell' scope='row'></th>
              <th className='table-header-cell'>{t('date')}</th>
              <th className='table-header-cell'>{t('value')}</th>
            </tr>
          </thead>
          <tbody>
            {/* Last Seen */}
            <tr className='table-smoked'>
              <th scope='row'>{t('last_seen')}</th>
              <td>{data.lastSeen}</td>
              <td></td>
            </tr>

            {/* Recent Analog Values */}
            <ValueRow
              label={t('recent_analog_values')}
              values={data.recentAnalogValues}
              className='table-grey71'
              isLoading={isLoading}
            />

            {/* Connectivity Values */}
            {[0, 1, 2].map((index) => (
              <ConnectivityRow
                key={`connectivity-${index}`}
                rssiValues={data.recentRSSIValues}
                rsrpValues={data.recentRSRPValues}
                rsrqValues={data.recentRSRQValues}
                berValues={data.recentBerValues}
                index={index}
                isLoading={isPartialLoading}
                t={t}
              />
            ))}

            {/* Battery Values */}
            <ValueRow
              label={t('recent_battery_values')}
              values={data.recentBatteryValues}
              showClassification={true}
              className='table-grey71'
              isLoading={isPartialLoading}
            />

            {/* C1 Values */}
            <ValueRow
              label={t('recent_c1_values')}
              values={data.recentC1Values}
              className='table-smoked'
              isLoading={isPartialLoading}
            />

            {/* Recent Errors */}
            <ValueRow
              label={t('recent_errors_values')}
              values={data.recent_errors}
              className='table-grey71'
              isLoading={isPartialLoading}
            />

            {/* Info Section */}
            <InfoRow label={t('transmitter_id')} value={data.transmitter_id} />
            <InfoRow
              label={t('transmitter_name')}
              value={data.transmitter_name}
            />
            <InfoRow
              label={t('distribution_unit')}
              value={data.distribution_name}
            />
            <InfoRow label={t('farming_unit')} value={data.farming_unit_name} />
            <InfoRow label={t('field')} value={data.field_name} />
            <InfoRow label={t('sensor_type')} value={data.type} />
            <InfoRow label={t('sensor_sub_type')} value={data.sub_type} />
            <InfoRow
              label={t('installation_date')}
              value={data.installation_date}
              isLoading={isPartialLoading}
            />
            <InfoRow
              label={t('modem_version')}
              value={modemTitle}
              isLoading={isPartialLoading}
            />
            <InfoRow
              label={t('iccid')}
              value={data.iccid}
              isLoading={isPartialLoading}
            />
            <InfoRow
              label={t('sensor_batch')}
              value={data.sensor_batch}
              isLoading={isPartialLoading}
            />
            <InfoRow
              label={t('transmission_interval')}
              value={data.transmission_interval}
              isLoading={isPartialLoading}
            />
            <InfoRow
              label={t('sample_interval')}
              value={data.sample_interval}
              isLoading={isPartialLoading}
            />
            <InfoRow
              label={t('micro_version')}
              value={data.micro_version}
              isLoading={isPartialLoading}
            />
            <InfoRow
              label={t('sim_last_event')}
              value={data.sim_last_event}
              isLoading={isPartialLoading}
            />
          </tbody>
        </table>
      </>
    );
  };

  return (
    <div className='container pt-5'>
      <div className='row justify-content-center'>
        <div className='col-12'>
          <h1 className='transmitter-test-title'>
            {t('transmitter_admin_page')}
          </h1>
        </div>
      </div>

      <div className='row justify-content-center'>
        <div className='col-12 align-self-center mt-3 pb-3 shadow-lg rounded transmitter-test-container'>
          <SearchContainer
            onTestClick={handleTestClick}
            loading={isLoading}
            initialTransmitterId={initialTransmitterId}
            onTabChange={handleTabChange}
          />
          <div className='transmitter-test-divider' />

          <div className='transmitter-test-content'>{renderContent()}</div>

          {partialError && (
            <div className='transmitter-test-partial-error'>
              <span className='transmitter-test-partial-error-icon'>⚠️</span>
              <span>{partialError}</span>
            </div>
          )}

          {(isLoading || isPartialLoading) && <LoadingIndicator t={t} />}
        </div>
      </div>
    </div>
  );
};

module.exports = connect()(TransmitterTest);
